import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { useDirection,useFeatureFlag, useInjection } from '@hooks';
import { FeatureFlagKey } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import {
  CaretDoubleLeftIcon,
  ChatIcon,
  CirclesFourIcon,
  ClipboardTextIcon,
  FileMagnifyingGlassIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react';
import { AuthStore, ConversationsHistoryStore } from '@stores';
import { NoFlipButton } from '@utils';

import { DI_TOKENS } from '../../constants';
import { Badge } from '../Badge';
import { TooltipCard } from '../Tooltip';

interface SearchPanelProps {
  onCollapse?: () => void;
}

export const SearchPanel: FC<SearchPanelProps> = observer(({ onCollapse }) => {
  const { dir } = useDirection();
  const { t } = useTrans();
  const navigate = useNavigate();
  const conversationsStore = useInjection<ConversationsHistoryStore>(
    DI_TOKENS.ConversationsHistoryStore,
  );
  const authStore = useInjection<AuthStore>(DI_TOKENS.AuthStore);
  const isPlatformAdmin = Boolean(authStore.userProfile?.isPlatformAdmin);
  const isRemediationEnabled = useFeatureFlag(FeatureFlagKey.REMEDIATION);

  const { pathname } = useLocation();

  const isDashboardRoute = pathname.includes('/dashboard');
  const isChatRoute = pathname.includes('/chat');
  const isAssessmentsRoute = pathname.includes('/assessments');
  const isRemediationRoute = pathname.startsWith('/remediation');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          value={conversationsStore.searchTerm}
          onChange={(e) => conversationsStore.setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <MagnifyingGlassIcon size={20} />,
              sx: {
                '.MuiInputBase-input': {
                  marginInlineStart: 1.5,
                },
              },
            },
          }}
          variant="outlined"
          placeholder={t('search.panel.search.placeholder')}
          sx={{ flex: 1 }}
        />
        {onCollapse && (
          <TooltipCard variant="dark" description="Collapse" placement="right">
            <IconButton
              size="small"
              onClick={onCollapse}
              sx={{
                borderRadius: 2,
                width: 36,
                height: 36,
                flexShrink: 0,
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'grey.200' },
              }}
            >
              <CaretDoubleLeftIcon size={16} />
            </IconButton>
          </TooltipCard>
        )}
      </Box>
      <Box
        sx={{
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Button
          startIcon={<CirclesFourIcon size={20} weight={isDashboardRoute ? 'fill' : 'regular'} />}
          onClick={() => navigate(routes.dashboard())}
          sx={{
            backgroundColor: isDashboardRoute ? 'grey.200' : 'transparent',
            color: isDashboardRoute ? 'text.primary' : 'black.300',
            py: 1.5,
            borderRadius: 3,
            justifyContent: 'flex-start',
            paddingInlineStart: 1.5,
            '.MuiButton-icon': {
              mx: 0,
            },
          }}
        >
          <Typography
            sx={{
              marginInlineStart: 1,
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            <Trans i18nKey="app.layout.sidebar.dashboard" />
          </Typography>
        </Button>
        <Button
          startIcon={<ChatIcon size={20} />}
          onClick={() => navigate('/chat')}
          sx={{
            backgroundColor: isChatRoute ? 'grey.200' : 'transparent',
            color: isChatRoute ? 'text.primary' : 'black.300',
            py: 1.5,
            borderRadius: 3,
            justifyContent: 'flex-start',
            paddingInlineStart: 1.5,
            '.MuiButton-icon': {
              mx: 0,
            },
          }}
        >
          <Typography
            sx={{
              marginInlineStart: 1,
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            <Trans i18nKey="search.panel.button.ask" />
          </Typography>
        </Button>
        <Button
          startIcon={<ClipboardTextIcon size={20} />}
          onClick={() => navigate(routes.assessmentList())}
          sx={{
            backgroundColor: isAssessmentsRoute ? 'grey.200' : 'transparent',
            color: isAssessmentsRoute ? 'text.primary' : 'black.300',
            py: 1.5,
            justifyContent: 'flex-start',
            borderRadius: 3,
            paddingInlineStart: 1.5,
            '.MuiButton-icon': {
              mx: 0,
            },
          }}
        >
          <Typography
            sx={{
              marginInlineStart: 1,
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            <Trans i18nKey="search.panel.button.assesments" />
          </Typography>
        </Button>
        {isRemediationEnabled && (
          <Button
            startIcon={<FileMagnifyingGlassIcon size={20} />}
            onClick={() => navigate(routes.remediationList())}
            sx={{
              backgroundColor: isRemediationRoute ? 'grey.200' : 'transparent',
              color: isRemediationRoute ? 'text.primary' : 'black.300',
              borderRadius: 3,
              justifyContent: 'flex-start',
              paddingInlineStart: 1.5,
              py: 1.5,
              '.MuiButton-icon': {
                mx: 0,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  marginInlineStart: 1,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                }}
              >
                <Trans i18nKey="search.panel.button.documentsRemediation" />
              </Typography>
              <Badge
                label={t('common.new')}
                sx={{
                  backgroundColor: 'transparent',
                  borderColor: 'grey.300',
                  color: '#0127ffff',
                  height: '24px',
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.65rem',
                  },
                }}
              />
            </Box>
          </Button>
        )}
        {/* Follows the app direction, so the icon sits at the inline start in
            both locales — matching the logical padding/margin used below. */}
        <NoFlipButton
          mode={dir}
          startIcon={<PlusCircleIcon weight="fill" size={20} />}
          onClick={() => {
            conversationsStore.deselectConversation();
            navigate('/chat');
          }}
          sx={{
            mt: 1.5,
            paddingInlineStart: 1,
            py: 1.5,
            backgroundColor: 'grey.200',
            borderRadius: 3,
            gap: 1,
            color: 'text.primary',
            '.MuiButton-icon': {
              mx: 0,
            },
          }}
        >
          <Typography
            sx={{ color: '#171717', marginInlineEnd: 1, fontWeight: 600, fontSize: '1rem' }}
          >
            <Trans i18nKey="search.panel.button.newchat" />
          </Typography>
        </NoFlipButton>

        {/* Platform-admin entry — only for platform_admin users. Lets an admin
            switch from their organization's app into the super-admin panel. */}
        {isPlatformAdmin && (
          <>
            <Box sx={{ height: '1px', backgroundColor: 'grey.200', my: 1.5 }} />
            <Button
              startIcon={<ShieldCheckIcon size={20} />}
              onClick={() => navigate(routes.admin())}
              sx={{
                backgroundColor: 'transparent',
                color: 'ocean.700',
                py: 1.5,
                borderRadius: 3,
                justifyContent: 'flex-start',
                paddingInlineStart: 1.5,
                '.MuiButton-icon': {
                  mx: 0,
                },
                '&:hover': { backgroundColor: 'grey.200' },
              }}
            >
              <Typography
                sx={{
                  marginInlineStart: 1,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                <Trans i18nKey="app.admin.nav.adminPanel" />
              </Typography>
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
});
